import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAnimals } from '../../contexts/AnimalsContext';

const FeaturedDogs: React.FC = () => {
  const { featuredAnimals, loading, error } = useAnimals();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
            <div className="h-48 bg-gray-200"></div>
            <div className="p-4">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 mb-4">Nu am putut încărca câinii din evidență momentan.</p>
        <p className="text-sm text-gray-500">Te rugăm să revii mai târziu sau să vizitezi pagina cu toți câinii.</p>
      </div>
    );
  }

  if (featuredAnimals.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 mb-4">În momentul de față nu avem câini disponibili pentru adopție.</p>
        <p className="text-sm text-gray-500">Te rugăm să revii mai târziu sau să ne contactezi pentru informații actualizate.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {featuredAnimals.map((dog, index) => (
        <motion.div
          key={dog.id}
          className="bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-lg transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
        >
          <div className="relative overflow-hidden">
            <img
              src={dog.image}
              alt={dog.name}
              className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/default-dog.svg';
              }}
            />
          </div>
          
          <div className="p-4">
            <div className="flex justify-between items-start mb-2">
              <Link to={`/caini/${dog.id}`} className="hover:text-accent transition-colors">
                <h3 className="text-lg font-bold text-primary">{dog.name}</h3>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
              <div>
                <span className="text-gray-500">Gen:</span>
                <span className="ml-1 font-medium">
                  {dog.gender === 'male' ? 'Mascul' : 'Femelă'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Vârstă:</span>
                <span className="ml-1 font-medium">{dog.age}</span>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 line-clamp-3 mb-4">
              {dog.description}
            </p>
            
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-1">
                {dog.vaccinated && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓
                  </span>
                )}
                {dog.neutered && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    S
                  </span>
                )}
                {dog.chipped && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    C
                  </span>
                )}
              </div>
            </div>
            
            <Link
              to={`/caini/${dog.id}`}
              className="w-full bg-primary text-white text-center py-2 px-4 rounded-md hover:bg-primary-dark transition-colors inline-block text-sm"
            >
              Vezi detalii
            </Link>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default FeaturedDogs;